import { inject, Injectable } from '@angular/core'
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  take,
  tap,
} from 'rxjs'

import { FirebaseError } from '@angular/fire/app'

import {
  collection,
  doc,
  docData,
  Firestore,
  collectionSnapshots,
} from '@angular/fire/firestore'

import { datelib, DateOnly, normalizeStat, log } from '@nx-superprep/utils'
import { IncludedModuleInfo, ModuleStats, OverallStats, RangedModuleStats, RangedOverallStats, UserStatsRange } from '../models/back-end'
import { LongtailService, StudyService } from './index'
import { _defaultChartEntry, _defaultCharts, ChartEntry, isSmaeChartEntry, PerformanceChartName, PerformanceCharts, PerformanceStat3Name, PerformanceStatName, PerformanceStatRecord, rangeToStatRange, StatEntry } from '../models/front-end'
import { isOverall } from '../models/front-end/performance/utils'

export type StatFormattertInput = Pick<StatEntry, 'name'|'value'> & Partial<{'count':number|string|undefined}>
export type StatFormatterOutput = Pick<StatEntry, 'name'|'value'> & {'count':string|undefined}

@Injectable({ providedIn: 'root' })
export class PerformanceService extends LongtailService {

  private _moduleStats: ModuleStats[] = []
  private _moduleRangedStats = new Map<string, ChartEntry>()
  private _headerContenStats = new Map<string, PerformanceStatRecord[]>()

  private _includedModules: IncludedModuleInfo[] = []

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _navigationState: any | undefined
  get navigationState() {
    return this._navigationState
  }
  set navigationState(value) {
    this._navigationState = value ? {...value, selection: {...value?.selection, didChange: true}} : undefined
    if (value?.selection) {
      //this.logPerformanceEvent(value)
    }
  }

  readonly firestore = inject(Firestore)
  readonly studyService = inject(StudyService)

  clear() {
    this._moduleStats = []
    this._moduleRangedStats?.clear()
    this._headerContenStats?.clear()
    this._includedModuleStats?.subscribe()
  }

  getCollection(collectionPath: string) {
    const ref = collection(this.firestore, collectionPath)
    return collectionSnapshots(ref).pipe(
      catchError((val: FirebaseError) => {
        log.debug(val.stack, collectionPath)
        return []
      }),
      map((res) =>
        res.map((data) => {
          const id = data.id
          const docData = data.data()
          return { ...docData, id }
        })
      )
    )
  }

  // stats

  get completedToday() {
    return this.studyService.completedPercentForDate()
  }

  get todayStats() {
    if (!this.userService.uid) { return of() }
    const date = new DateOnly().toISOString()
    return docData(doc(this.firestore, `${RangedOverallStats.collectionPath(this.userService.uid, UserStatsRange.Daily)}/${date}`)) as Observable<OverallStats>
  }

  get overallStats() {
    if (!this.userService.uid) { return of() }
    return docData(doc(this.firestore, `${OverallStats.collectionPath()}/${this.userService.uid}`)) as Observable<OverallStats>
  }

  get modulesStats() {
    if (!this.userService.uid) { return of() }
    const ref = collection(this.firestore, `${ModuleStats.collectionPath(this.userService.uid)}` )
    return (ref ? collectionSnapshots(ref) : of([])).pipe(
      map((stats) => stats.map((c) => ModuleStats.converter.fromFirestore(c))),
      tap((stats) => (this._moduleStats = stats))
    )
  }

  // modules stats BEGIN ---------------------------------------------------------------------------------

  get _includedModuleStats() {
    return this.studyService?.includedModules?.pipe(
      map((ms) => ms.map((m) => new ModuleStats(m))),
      tap((stats) => (this._moduleStats = stats))
    )
  }

  get includedModules() {
    if (!this.userService.uid) { return of() }
    const ref = collection(this.firestore, `${IncludedModuleInfo.collectionPath(this.userService.uid)}`)
    return merge(
      of(this._includedModules),
      (ref ? collectionSnapshots(ref) : of([])).pipe(
        map((ms) => ms.map((m) => new IncludedModuleInfo(m.id, m.data()['includedDate'].toDate()))),
        tap((m) => (this._includedModules = m))
      )
    )
  }

  fetchModuleStats(onlyIncluded = false, uid = this.userService.uid) {
    if (!uid) { return of() }
    const source = merge(of(this._moduleStats), this.modulesStats)
    if (!onlyIncluded) { return source }
    const sourceIds = this.includedModules.pipe(map((ms) => ms.map((m) => m.moduleId)))
    return combineLatest([source, sourceIds]).pipe(
      map(([ms, ids]) => ms.filter((m) => ids.includes(m.moduleId ?? '')))
    )
  }

  // modules stats END ---------------------------------------------------------------------------------
  // global module charts BEGIN ------------------------------------------------------------------------

  private _globalStatsHash(
    date: DateOnly = new DateOnly()
  ) {
    return `${date.toISOString()}-'GLOBAL'`
  }

  private _fetchGlobalStats(uid = this.userService.uid) {
    if (!uid) { return of() }
    return combineLatest([this.todayStats, this.overallStats]).pipe(
      map((stats) => {
        const todayStats = stats[0] ?? new OverallStats(uid)
        const overallStats = stats[1] ?? new OverallStats(uid)
        const todayContent = this._defaultHeaderContent('today', this._calculateHeaderValues(todayStats));
        const overallContent = this._defaultHeaderContent('overall', this._calculateHeaderValues(overallStats));
        return [...todayContent, ...overallContent];
      })
    )
  }

  getGlobalStats() {
    const hash = this._globalStatsHash()
    const cached = this._headerContenStats.get(hash)
    const fetched = this._fetchGlobalStats().pipe(tap((stats) => this._headerContenStats.set(hash, stats)))
    return cached ? merge(of(cached),fetched) : fetched
  }

  private _calculateHeaderValues(stats: OverallStats): {
    retention: number;
    correct: number;
    count: number;
    completed: number;
    studied: number;
  } {
    const retention = Math.max(0, Math.min(100, Math.round((100 * stats.rightReviewCount) / Math.max(1, stats.rightReviewCount + stats.wrongReviewCount))));
    const correct = stats.rightAcquisitionCount + stats.rightReviewCount;
    const count = correct + stats.wrongAcquisitionCount + stats.wrongReviewCount;
    const completed = Math.max(0, Math.min(100, Math.round(100 * (stats.progress ?? 0))));
    const studied = stats.studyTime;

    return { retention, correct, count, completed, studied };
  }

  private _defaultHeaderContent(range?: PerformanceStat3Name, values: {
    retention: number;
    correct: number;
    count: number;
    completed: number;
    studied: number;
  } = { retention: 0, correct: 0, count: 0, completed: 0, studied: 0 }): PerformanceStatRecord[] {
    return Array(range ? 1 : 2)
      .fill(1)
      .map((_, index) => ({
        range: index  ? 'overall' : range ?? 'today',
        stats: [
          { name: 'retention', value: values.retention, color: 'black', background: 'rgba(49, 175, 212, 0.04)' },
          { name: 'correct', value: values.correct, count: values.count, color: 'black', background: 'rgba(94, 43, 255, 0.04)' },
          { name: 'completed', value: values.completed, color: 'black', background: 'rgba(242, 100, 25, 0.04)' },
          { name: 'studied', value: values.studied, color: 'black', background: 'rgba(0, 106, 99, 0.04)' },
        ].map((s) => this._statFormatter(s as unknown as StatFormattertInput)),
      }));
  }

  private _statFormatter(s: StatFormattertInput): StatFormatterOutput {
    return {
      ...(s as StatFormatterOutput),
      value: normalizeStat(s.value, s.name === 'studied' ? 'duration' : (s.name === 'correct' && (Number(s.value) || 0) < 10000) ? undefined : 'number'),
      count: s.count ? normalizeStat(s.count ?? 0, (s.name === 'correct' && (Number(s.value) || 0) < 10000) ? undefined : 'number') : undefined,
    };
  }

  // global module charts END --------------------------------------------------------------------------
  // ranged module charts BEGIN ------------------------------------------------------------------------

  private _updateChartEntryValues(r: ChartEntry, performanceCharts: PerformanceCharts[], selected: string) {
    //log.debug(`${moduleId} ${r.name}`,r)
    const labels = this._getLabelsForChart(r)
    // time
    const timeChart: PerformanceCharts = performanceCharts.find((c) => c.title === 'Time Studied') ?? performanceCharts[0]
    const time: number[] = timeChart.data.datasets[0].data
    // correct
    const correctChart: PerformanceCharts = performanceCharts.find((c) => c.title === 'Correct Answer') ?? performanceCharts[1]
    const correct: number[] = (correctChart.data.datasets.find((c) => c.label === 'correct') ?? correctChart.data.datasets[0]).data
    const wrong: number[] = (correctChart.data.datasets.find((c) => c.label === 'wrong') ?? correctChart.data.datasets[1]).data
    // retention
    const retentionChart: PerformanceCharts = performanceCharts.find((c) => c.title === 'Retention Rate') ?? performanceCharts[2]
    const retention: number[] = retentionChart.data.datasets[0].data
    // Overall visibility
    switch (selected) {
      case 'retention': {
        [timeChart, correctChart].forEach((c) => (c.hidden = true))
        retentionChart.hidden = false
      } break
      case 'correct': {
        [timeChart, retentionChart].forEach((c) => (c.hidden = true))
        correctChart.hidden = false
      } break
      case 'completed': {
        [timeChart, retentionChart].forEach((c) => (c.hidden = true))
        correctChart.hidden = false
      } break
      case 'studied': {
        [correctChart, retentionChart].forEach((c) => (c.hidden = true))
        timeChart.hidden = false
      } break
      default: {
        [timeChart, correctChart, retentionChart].forEach((c) => (c.hidden = false))
      }
    }
    // set labels
    [timeChart, correctChart, retentionChart].forEach((c) => (c.data.labels = labels))
    // set entries
    r.entries.forEach((e, i) => {
      time[i] = e.studyTime
      correct[i] = e.rightAcquisitionCount + e.rightReviewCount
      wrong[i] = e.wrongAcquisitionCount + e.wrongReviewCount
      retention[i] = e.rightReviewCount / Math.max(1, e.rightReviewCount + e.wrongReviewCount)
    })
  }

  private _getLabelsForChart(r: ChartEntry) {
    const unit = r.name === 'year' ? 'MMM' : r.name === 'month' ? 'DD' : 'ddd'
    const start = datelib().add(1, r.step).startOf(r.step).subtract(r.count, r.step)
    return Array(r.count).fill(1).map((_element, index) => start.add(index, r.step).format(unit))
  }

  private _rangedStatsHash(
    id: string | undefined,
    range = UserStatsRange.Daily,
    date: DateOnly = new DateOnly()
  ) {
    return `${date.toISOString()}-${id ?? 'Overall'}-${range}`.toUpperCase()
  }

  private _fetchModuleCharts(
    moduleId: string | undefined,
    range: UserStatsRange,
    date: DateOnly = new DateOnly(),
    uid = this.userService.uid
  ) {
    if (!uid) { return of() }
    const defaultChartEntry = _defaultChartEntry(moduleId, range, date);
    const collectionType = isOverall(moduleId) ? RangedOverallStats : RangedModuleStats
    const refPath = collectionType.collectionPath(uid, defaultChartEntry.path)
    const sources: Observable<RangedModuleStats | RangedOverallStats>[] = defaultChartEntry.entries
      .map((entry) => {
        const ref = doc(this.firestore, `${refPath}/${isOverall(moduleId) ? entry.id?.replace('-OVERALL', '') : entry.id}`)
        return ref
          ? docData(ref).pipe(
              catchError((error: FirebaseError) => { log.debug(error.stack); return [] }),
              take(1),
              map((data) => {
                const rangedStats = moduleId && !isOverall(moduleId)
                  ? new RangedModuleStats(moduleId, defaultChartEntry.path, entry.startDate)
                  : new RangedOverallStats(defaultChartEntry.path, entry.startDate)
                if (data) {Object.assign(rangedStats, data['data'] ? data['data']() : data)}
                return rangedStats;
              }
            ))
          : of(moduleId && !isOverall(moduleId)
            ? new RangedModuleStats(moduleId, defaultChartEntry.path, entry.startDate)
            : new RangedOverallStats(defaultChartEntry.path, entry.startDate))
      })
    return forkJoin(sources).pipe(
      map((entries) => ({ ...defaultChartEntry, entries }) as ChartEntry),
    )
  }

  private _fetchRangedModuleCharts(
    id: string | undefined,
    range = UserStatsRange.Daily,
    date: DateOnly = new DateOnly(),
    uid = this.userService.uid
  ) {
    if (!uid) { return of() }
    const hash = this._rangedStatsHash(id, range, date)
    const cached = this._moduleRangedStats.get(hash)
    const fetched = this._fetchModuleCharts(id, range, date).pipe(tap((chart) => this._moduleRangedStats.set(hash, chart)))
    return cached ? merge(of(cached),fetched) : fetched
  }

  getModuleCharts(
    moduleId = 'Overall',
    selected: PerformanceStatName,
    range?: PerformanceChartName
  ) {
    const rangedCharts = _defaultCharts()
    const sources = (range
      ? [rangeToStatRange(range)]
      : [UserStatsRange.Daily, UserStatsRange.Monthly, UserStatsRange.Yearly]).map((range) => this._fetchRangedModuleCharts(moduleId, range))
    return combineLatest(sources).pipe(
      distinctUntilChanged(isSmaeChartEntry),
      map((rs) => {
        rs.forEach((r: ChartEntry) => {
          const performanceCharts = rangedCharts.find((c) => c.range === r.name)?.charts
          if (!performanceCharts) { return }
          this._updateChartEntryValues(r, performanceCharts, selected)
        })
        return rangedCharts
      })
    )
  }
}

